import { useWebchatQueueStats } from '@/hooks/use-webchat-queue-stats/use-webchat-queue-stats';
import { useUser } from '@/hooks/use-user/use-user';
import { toggleAvailability } from '@/services/webchat-service/webchat.service';
import { useWebchatStore } from '@/store/webchat.store';
import { SoundOutlined, SoundFilled } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Button, Select, Switch, Tooltip } from 'antd';
import CustomSwitch from '@/components/custom-switch/custom-switch';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { formatNumber } from '@/i18n/format';
import styles from './webchat-header.module.scss';

const QUEUED_WARN_THRESHOLD = 1;
const WAIT_WARN_THRESHOLD_SEC = 60;
const WAIT_CRITICAL_THRESHOLD_SEC = 180;

interface WebchatHeaderProps {
  onToggleTranslate?: (conversationId: number, translate: boolean) => void;
  onSetAgentLanguage?: (conversationId: number, language: string) => void;
}

// `value` is the language code the API expects; the label is looked up at render time.
const LANGUAGE_OPTIONS: { value: string; labelKey: ParseKeys<'webchat'> }[] = [
  { value: 'pl-PL', labelKey: 'header.languages.polish' },
  { value: 'en-us', labelKey: 'header.languages.english' },
  { value: 'es-mx', labelKey: 'header.languages.spanish' },
];

export const WebchatHeader = ({ onToggleTranslate, onSetAgentLanguage }: WebchatHeaderProps) => {
  const { t } = useTranslation('webchat');
  const { queueStats } = useWebchatQueueStats();
  const { userData } = useUser();
  // Per-field Zustand selectors instead of the destructured-whole-store
  // subscription this used before. Reading the entire store made the
  // header re-render on every WebSocket event (session_queued,
  // session_closed, transcript turns, typing indicators) because each
  // of those mutates `sessions`. The header doesn't need to re-render
  // for any of that — it only depends on activeTab, the three
  // translate/sound flags, and the action functions (which are stable
  // references in Zustand). `selectedConversationId` is needed to
  // gate the translate-toggle callback, but it changes rarely (only on
  // card click) so the subscription is cheap. `sessions` is no longer
  // subscribed — we read it on-demand inside the click handler via
  // `useWebchatStore.getState()`.
  const activeTab = useWebchatStore((s) => s.activeTab);
  const setActiveTab = useWebchatStore((s) => s.setActiveTab);
  const soundEnabled = useWebchatStore((s) => s.soundEnabled);
  const toggleSound = useWebchatStore((s) => s.toggleSound);
  const translateEnabled = useWebchatStore((s) => s.translateEnabled);
  const toggleTranslate = useWebchatStore((s) => s.toggleTranslate);
  const targetLanguage = useWebchatStore((s) => s.targetLanguage);
  const setTargetLanguage = useWebchatStore((s) => s.setTargetLanguage);
  const selectedConversationId = useWebchatStore((s) => s.selectedConversationId);
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    if (userData?.webchat_available !== undefined) {
      setIsAvailable(userData.webchat_available);
    }
  }, [userData?.webchat_available]);

  const availabilityMutation = useMutation({
    mutationFn: toggleAvailability,
    onSuccess: (data) => {
      setIsAvailable(data.webchat_available);
    },
  });

  const formatWait = (seconds: number) => {
    if (seconds < 60) return t('header.wait.seconds', { value: formatNumber(seconds) });
    return t('header.wait.minutes', { value: formatNumber(Math.floor(seconds / 60)) });
  };

  return (
    <div className={styles.header}>
      <h1 className={styles.title}>{t('header.title')}</h1>
      <CustomSwitch
        initialActive={activeTab === 'live' ? 'Live' : 'History'}
        options={['Live', 'History']}
        variant="small"
        onSwitch={(val) => setActiveTab(val === 'Live' ? 'live' : 'history')}
      />
      <div className={styles.controls}>
        <div className={styles.controlsCapsule}>
          <Tooltip
            title={
              isAvailable
                ? t('header.availability.availableTooltip')
                : t('header.availability.unavailableTooltip')
            }
          >
            <button
              type="button"
              className={clsx(styles.availabilityPill, isAvailable && styles.availabilityOn)}
              onClick={() => availabilityMutation.mutate()}
              disabled={availabilityMutation.isPending}
            >
              <span className={styles.statusDot} />
              <span>
                {isAvailable ? t('header.availability.available') : t('header.availability.unavailable')}
              </span>
            </button>
          </Tooltip>
          <span className={styles.controlsDivider} />
          <div className={styles.translateToggle}>
            <Switch
              checked={translateEnabled}
              onChange={(checked) => {
                toggleTranslate();
                if (selectedConversationId) {
                  const session = useWebchatStore.getState().sessions.find((s) => s.id === selectedConversationId);
                  if (session?.webchat_status === 'live') {
                    if (onToggleTranslate) onToggleTranslate(selectedConversationId, checked);
                    // When enabling translate, immediately sync the current target language
                    // so the LLM bridge uses the right language from the very first STT turn.
                    if (checked && onSetAgentLanguage) onSetAgentLanguage(selectedConversationId, targetLanguage);
                  }
                }
              }}
              size="small"
            />
            <span className={styles.availabilityLabel}>{t('header.translate')}</span>
            <Select
              size="small"
              value={targetLanguage}
              options={LANGUAGE_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
              onChange={(lang) => {
                setTargetLanguage(lang);
                if (onSetAgentLanguage && selectedConversationId) {
                  const session = useWebchatStore.getState().sessions.find((s) => s.id === selectedConversationId);
                  if (session?.webchat_status === 'live') {
                    onSetAgentLanguage(selectedConversationId, lang);
                  }
                }
              }}
              style={{
                minWidth: 90,
                visibility: translateEnabled ? 'visible' : 'hidden',
              }}
            />
          </div>
          <span className={styles.controlsDivider} />
          <Tooltip title={soundEnabled ? t('header.sound.on') : t('header.sound.off')}>
            <Button
              type="text"
              size="small"
              className={clsx(styles.soundButton, !soundEnabled && styles.soundButtonMuted)}
              icon={soundEnabled ? <SoundFilled /> : <SoundOutlined />}
              onClick={toggleSound}
              aria-label={soundEnabled ? t('header.sound.mute') : t('header.sound.unmute')}
            />
          </Tooltip>
        </div>
      </div>
      {queueStats && (
        <div className={styles.statsRow}>
          <div className={styles.heroGroup}>
            <Tooltip title={t('header.stats.queuedTooltip')}>
              <div
                className={clsx(
                  styles.heroCard,
                  queueStats.queued >= QUEUED_WARN_THRESHOLD && styles.heroWarn,
                )}
              >
                <span className={styles.heroLabel}>{t('header.stats.queued')}</span>
                {/*
                  No `key={queueStats.queued}` here on purpose. The
                  earlier version forced React to unmount/remount this
                  span on every numerical change, which replayed the
                  0.6s `heroPulse` scale animation — visible as the
                  Queued number "pulsing" loudly on every session_queued
                  or session_closed broadcast. Letting React reconcile
                  the text-node update in place keeps the number stable.
                */}
                <span className={styles.heroValue}>
                  {formatNumber(queueStats.queued)}
                </span>
              </div>
            </Tooltip>
            <Tooltip title={t('header.stats.longestWaitTooltip')}>
              <div
                className={clsx(
                  styles.heroCard,
                  queueStats.longest_wait >= WAIT_CRITICAL_THRESHOLD_SEC && styles.heroCritical,
                  queueStats.longest_wait >= WAIT_WARN_THRESHOLD_SEC &&
                    queueStats.longest_wait < WAIT_CRITICAL_THRESHOLD_SEC &&
                    styles.heroWarn,
                )}
              >
                <span className={styles.heroLabel}>{t('header.stats.longestWait')}</span>
                <span className={styles.heroValue}>{formatWait(queueStats.longest_wait)}</span>
              </div>
            </Tooltip>
          </div>
          <div className={styles.miniGroup}>
            <div className={clsx(styles.miniStat, styles.miniLive)}>
              <span className={styles.miniDot} />
              <span className={styles.miniLabel}>{t('header.stats.live')}</span>
              <span className={styles.miniValue}>{formatNumber(queueStats.live)}</span>
            </div>
            <div className={clsx(styles.miniStat, styles.miniBot)}>
              <span className={styles.miniDot} />
              <span className={styles.miniLabel}>{t('header.stats.bot')}</span>
              <span className={styles.miniValue}>{formatNumber(queueStats.bot)}</span>
            </div>
            {queueStats.voice !== undefined && (
              <div className={clsx(styles.miniStat, styles.miniVoice)}>
                <span className={styles.miniDot} />
                <span className={styles.miniLabel}>{t('header.stats.voice')}</span>
                <span className={styles.miniValue}>{formatNumber(queueStats.voice)}</span>
              </div>
            )}
            {queueStats.sms !== undefined && (
              <div className={clsx(styles.miniStat, styles.miniSms)}>
                <span className={styles.miniDot} />
                <span className={styles.miniLabel}>{t('header.stats.sms')}</span>
                <span className={styles.miniValue}>{formatNumber(queueStats.sms)}</span>
              </div>
            )}
            <div className={clsx(styles.miniStat, styles.miniAgents)}>
              <span className={styles.miniDot} />
              <span className={styles.miniLabel}>{t('header.stats.agents')}</span>
              <span className={styles.miniValue}>{formatNumber(queueStats.available_agent_count)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
